/**
 * Word Labyrinth — the maze generator (specs/labyrinth-mode.md §7).
 *
 * Everything about the *shape* of a board lives here; `deal.ts` decides what words go on it and
 * `LabyrinthScene` only draws what the two return. Phaser-free so it can be tested in node, exactly
 * like `game/maze/grid.ts` and `game/pairs/deal.ts`.
 *
 * Two rules carry this file and both are easy to get backwards:
 *
 *  1. **The maze must NOT have one solution** (§7.1). Every textbook generator — recursive
 *     backtracker, Prim, Kruskal — produces a *perfect* maze, exactly one route between any two
 *     cells. A perfect maze here is solvable with the words switched off: follow the only corridor
 *     that reaches the gate and read nothing. So this one braids, and `minExitRoutes` is checked by
 *     search rather than assumed by construction.
 *
 *  2. **No corridor advances leftward** (§7.3) — not on the true path and not in the dead ends
 *     either. Every horizontal edge joins column `c` to column `c + 1`, and `walk.ts` only ever
 *     traverses one rightward except when retracing. Restricting only the true path would leak the
 *     answer: a child who noticed could eliminate every leftward branch without reading it.
 */
import { LABYRINTH, gridFor, pathCellsFor, forksFor, bendsFor, timeForRound } from './config';

/** `[col, row]`. Column 0 is the entrance edge; column `cols - 1` is the gate edge. */
export type Cell = readonly [number, number];

export interface LabyrinthBoard {
  round: number;
  cols: number;
  rows: number;
  /** `grid[row][col]` — one whole word per cell. */
  grid: string[][];
  /** The true path, in reading order: entrance edge → gate edge. */
  path: Cell[];
  /** Open corridors, as canonical edge keys. Everything not in here is a wall. */
  open: Set<string>;
  /** Row the entrance arch sits on (`path[0]`'s row) and the row the gate sits on. */
  entryRow: number;
  gateRow: number;
  /** The words along `path`, in order — what the strip must read for the gate to open. */
  sentence: string[];
  /** Index after which a full stop / gate post is drawn on a chained board; `-1` if none. */
  postAfter: number;
  timeMs: number;
}

export type Rng = () => number;

/** Canonical key for the undirected corridor between two adjacent cells. */
export function edgeKey(a: Cell, b: Cell): string {
  const [c1, r1] = a;
  const [c2, r2] = b;
  return c1 < c2 || (c1 === c2 && r1 <= r2)
    ? `${c1},${r1}-${c2},${r2}`
    : `${c2},${r2}-${c1},${r1}`;
}

const key = (c: Cell): string => `${c[0]},${c[1]}`;

/** The two moves a corridor may ever be: one column right, or one row up/down in the same column. */
function neighboursOf([c, r]: Cell, cols: number, rows: number): Cell[] {
  const out: Cell[] = [];
  if (c + 1 < cols) out.push([c + 1, r]);
  if (c - 1 >= 0) out.push([c - 1, r]);
  if (r + 1 < rows) out.push([c, r + 1]);
  if (r - 1 >= 0) out.push([c, r - 1]);
  return out;
}

function shuffle<T>(xs: T[], rng: Rng): T[] {
  const a = xs.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * How many distinct routes to the gate a board of this size must offer (§7.1).
 *
 * Three wherever there is room, two on the smallest grids — a 3-word sentence gives a 2x3 board and
 * six cells cannot hold three routes. Two is still braided, which is the property that matters:
 * wall-following cannot pick the answer. Exported so the tests assert the real rule instead of a
 * copy of it that can drift.
 */
export function routesNeededFor(cols: number, rows: number): number {
  return cols * rows >= 9 ? LABYRINTH.minExitRoutes : 2;
}

/**
 * The true path: a self-avoiding walk from the left edge to the right edge of exactly `len` cells,
 * advancing rightward or vertically and never leftward.
 *
 * Because it never goes left, the column sequence is non-decreasing and the number of vertical
 * steps is fixed by arithmetic: crossing `cols` columns costs `cols - 1` rightward steps, so
 * exactly `len - cols` steps are vertical. Nothing here can change that count — only `buildMaze`'s
 * choice of `cols` can.
 *
 * What this function CAN change is where they fall, and that is what the player feels. Four
 * vertical steps stacked in one column is a single long corridor and two turns; four spread across
 * four columns is eight. So the walk alternates on purpose: after a rightward step it turns if it
 * still has a vertical to spend, and after a vertical step it prefers to move on. A vertical run
 * may not reverse inside its own column — that would revisit a cell — so `vdir` is pinned on the
 * first vertical move and cleared by every rightward one.
 *
 * Returns `null` when the shape is impossible (`len < cols`, or the walk paints itself into a
 * corner), and the caller re-rolls.
 */
export function carvePath(cols: number, rows: number, len: number, rng: Rng): Cell[] | null {
  if (len < cols || len > cols * rows) return null;
  const path: Cell[] = [];
  const seen = new Set<string>();
  let c = 0;
  let r = Math.floor(rng() * rows);
  let vdir = 0;
  let vLeft = len - cols;    // vertical steps still to place
  let hLeft = cols - 1;      // rightward steps still to place
  let lastVertical = false;
  path.push([c, r]);
  seen.add(`${c},${r}`);

  for (let step = 1; step < len; step++) {
    const vOptions = (vdir === 0 ? [-1, 1] : [vdir]).filter((d) => {
      const nr = r + d;
      return nr >= 0 && nr < rows && !seen.has(`${c},${nr}`);
    });
    const canV = vLeft > 0 && vOptions.length > 0;
    const canH = hLeft > 0;
    if (!canV && !canH) return null;

    let goVertical: boolean;
    if (!canH) goVertical = true;
    else if (!canV) goVertical = false;
    // Alternate. Turning straight after a turn just lengthens one corridor; turning after a
    // straight run is what puts a decision in front of the child.
    else goVertical = lastVertical ? rng() < 0.15 : rng() < 0.82;

    if (goVertical) {
      const d = vOptions[Math.floor(rng() * vOptions.length)];
      r += d;
      vdir = d;
      vLeft -= 1;
    } else {
      c += 1;
      vdir = 0;
      hLeft -= 1;
    }
    lastVertical = goVertical;
    if (seen.has(`${c},${r}`)) return null;
    path.push([c, r]);
    seen.add(`${c},${r}`);
  }
  return path[path.length - 1][0] === cols - 1 && vLeft === 0 ? path : null;
}

/** How many times a path changes direction — what a player experiences as a turn. */
export function turnsIn(path: Cell[]): number {
  let turns = 0;
  for (let i = 2; i < path.length; i++) {
    const prev = path[i - 1][0] - path[i - 2][0] === 1;
    const cur = path[i][0] - path[i - 1][0] === 1;
    if (prev !== cur) turns += 1;
  }
  return turns;
}

/**
 * Every simple route from `from` to `to`, capped at `cap` so a pathological board cannot hang the
 * deal. Used twice: to prove the maze is braided (§7.1) and to prove no other route spells the
 * sentence (§7.2).
 */
export function routesBetween(
  from: Cell,
  to: Cell,
  cols: number,
  rows: number,
  open: Set<string>,
  cap = 200,
): Cell[][] {
  const out: Cell[][] = [];
  const trail: Cell[] = [];
  const onTrail = new Set<string>();

  const walk = (at: Cell): void => {
    if (out.length >= cap) return;
    trail.push(at);
    onTrail.add(key(at));
    if (at[0] === to[0] && at[1] === to[1]) {
      out.push(trail.slice());
    } else {
      for (const nb of neighboursOf(at, cols, rows)) {
        // Forward only: a route never advances leftward (§7.3).
        if (nb[0] < at[0]) continue;
        if (onTrail.has(key(nb))) continue;
        if (!open.has(edgeKey(at, nb))) continue;
        walk(nb);
      }
    }
    trail.pop();
    onTrail.delete(key(at));
  };

  walk(from);
  return out;
}

/** Cells reachable from `from` by forward moves only — nothing may be walled off (§7.1 step 5). */
function reachable(from: Cell, cols: number, rows: number, open: Set<string>): Set<string> {
  const seen = new Set<string>([key(from)]);
  const queue: Cell[] = [from];
  while (queue.length) {
    const at = queue.shift() as Cell;
    for (const nb of neighboursOf(at, cols, rows)) {
      if (nb[0] < at[0]) continue;
      if (!open.has(edgeKey(at, nb))) continue;
      if (seen.has(key(nb))) continue;
      seen.add(key(nb));
      queue.push(nb);
    }
  }
  return seen;
}

/**
 * The wrong turns off a path, and how much corridor each one leads to.
 *
 * A **wrong turn** is a step from a cell on the true path onto a cell that is not — the thing
 * `walk.ts` charges a battery for (§5.3). Its **branch** is everything reachable onward from there
 * without stepping back onto the path, and §7.1 says that must be at least `minDeadEndLen` cells:
 * a child who just paid for a corridor has to have something to read in it.
 */
export function branchesOf(
  path: Cell[],
  cols: number,
  rows: number,
  open: Set<string>,
): { from: Cell; to: Cell; size: number }[] {
  const onPath = new Set(path.map(key));
  const out: { from: Cell; to: Cell; size: number }[] = [];
  for (const cell of path) {
    for (const nb of neighboursOf(cell, cols, rows)) {
      if (nb[0] < cell[0]) continue;
      if (onPath.has(key(nb))) continue;
      if (!open.has(edgeKey(cell, nb))) continue;
      const seen = new Set<string>([key(nb)]);
      const queue: Cell[] = [nb];
      while (queue.length) {
        const at = queue.shift() as Cell;
        for (const n2 of neighboursOf(at, cols, rows)) {
          if (n2[0] < at[0]) continue;
          if (onPath.has(key(n2))) continue;
          if (!open.has(edgeKey(at, n2))) continue;
          if (seen.has(key(n2))) continue;
          seen.add(key(n2));
          queue.push(n2);
        }
      }
      out.push({ from: cell, to: nb, size: seen.size });
    }
  }
  return out;
}

/**
 * Carve one maze around a fixed path.
 *
 * Order matters: the path's own corridors are opened first and are never removed, then the rest of
 * the grid is braided around them. Braiding by *adding* corridors to a fixed skeleton — rather than
 * carving a tree and knocking walls out afterwards — is what guarantees the true path survives
 * whatever the braid does.
 */
function carveMaze(
  round: number,
  path: Cell[],
  cols: number,
  rows: number,
  rng: Rng,
): { open: Set<string>; routes: number } | null {
  const open = new Set<string>();
  for (let i = 1; i < path.length; i++) open.add(edgeKey(path[i - 1], path[i]));

  // Every candidate corridor in the grid, shuffled. Horizontal edges only ever join c to c+1, so
  // there is no leftward corridor to exclude — the geometry forbids it rather than a filter.
  const candidates: [Cell, Cell][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (c + 1 < cols) candidates.push([[c, r], [c + 1, r]]);
      if (r + 1 < rows) candidates.push([[c, r], [c, r + 1]]);
    }
  }

  const onPath = new Set(path.map(key));
  const target = path[path.length - 1];

  // Pass 1: connect everything. A cell nobody can reach holds a word nobody can read.
  for (const [a, b] of shuffle(candidates, rng)) {
    if (open.has(edgeKey(a, b))) continue;
    const seen = reachable(path[0], cols, rows, open);
    if (seen.size === cols * rows) break;
    // Open it if it brings in a cell that was cut off, or with a small chance anyway so the maze
    // gets loops rather than a tree with the path threaded through it.
    if (!seen.has(key(a)) || !seen.has(key(b)) || rng() < 0.28) open.add(edgeKey(a, b));
  }

  // Pass 2: braid until there are enough distinct ways to the gate (§7.1 step 3).
  //
  // Three routes is the target, but the smallest boards cannot hold three. A 3-word sentence gives
  // a 2x3 grid — six cells, three of them the path — and there is simply not enough room. Those
  // rungs settle for two, which is still braided: wall-following cannot pick the answer, which is
  // the property that matters. ja and ko force the case rather than being an edge of it, since
  // between them they have 14 sentences longer than three tiles.
  const needRoutes = routesNeededFor(cols, rows);
  let routes = routesBetween(path[0], target, cols, rows, open).length;
  for (const [a, b] of shuffle(candidates, rng)) {
    if (routes >= needRoutes) break;
    if (open.has(edgeKey(a, b))) continue;
    open.add(edgeKey(a, b));
    routes = routesBetween(path[0], target, cols, rows, open).length;
  }

  // Pass 3: no stub branches (§7.1 step 4). A one-cell dead end is visibly a stub — a child who
  // has just paid a battery for a corridor must have something to read in it.
  //
  // Growing is the preferred fix, because sealing costs a fork and forks are the ladder's only
  // difficulty lever from round 3 on. But growing is not always possible: when the sentence is
  // exactly as long as the grid is wide the path has no slack to wind, so it runs dead straight,
  // and a branch hanging off its last column can be boxed in by the grid edge on three sides. That
  // is the ja/ko/th case — their sentences top out at four tiles — so it is the common case, not a
  // corner one. There the stub is sealed and the cell re-connected from its left-hand neighbour,
  // which keeps it readable while making it part of a branch entered somewhere else.
  for (let attempt = 0; attempt < 4; attempt++) {
    const short = branchesOf(path, cols, rows, open).filter((b) => b.size < LABYRINTH.minDeadEndLen);
    if (short.length === 0) break;
    for (const b of short) {
      const forward = shuffle(neighboursOf(b.to, cols, rows), rng)
        .filter((nb) => nb[0] >= b.to[0] && !onPath.has(key(nb)));
      if (forward.length > 0) {
        open.add(edgeKey(b.to, forward[0]));
        continue;
      }
      open.delete(edgeKey(b.from, b.to));
      const inbound = shuffle(neighboursOf(b.to, cols, rows), rng).filter((nb) => !onPath.has(key(nb)));
      if (inbound.length > 0) open.add(edgeKey(inbound[0], b.to));
    }
  }

  // Pass 4: nothing may be walled off. Sealing a stub can orphan the cell behind it, so
  // reachability is repaired last and checked rather than assumed.
  for (let attempt = 0; attempt < 4; attempt++) {
    const seen = reachable(path[0], cols, rows, open);
    if (seen.size === cols * rows) break;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (seen.has(`${c},${r}`)) continue;
        // Prefer an inbound corridor from a reachable NON-path cell: hanging an orphan straight
        // off the true path would create the very stub pass 3 just removed.
        const nbs = shuffle(neighboursOf([c, r], cols, rows), rng);
        const from = nbs.find((nb) => seen.has(key(nb)) && !onPath.has(key(nb)))
          ?? nbs.find((nb) => seen.has(key(nb)));
        if (from) open.add(edgeKey(from, [c, r]));
      }
    }
  }

  // Pass 5, and it must be LAST: one choice at every word.
  //
  // This was pass 2b and it was in the wrong place — passes 3 and 4 both add corridors, so a cell
  // normalised early could pick up a second branch afterwards, and the reachability repair could
  // even hang one off the final word. Fork shape is the last thing decided because it is the thing
  // every earlier pass can disturb.
  //
  // The rule is per CELL, not per board. It was a cap once — braiding offers up to nine ways off the
  // path and the ladder trimmed them — but capping alone left most of the path with no branch at
  // all: measured, 2 of 4 cells at round 1 and 3 of 9 at round 3. A cell with one legal move is not
  // a decision, it is a corridor, and a child who tries to pick a wrong word there finds the board
  // will not let them. That is what "it's not possible to choose the wrong one" meant.
  //
  // So: every word except the last offers exactly one wrong turn wherever geometry allows, the last
  // offers none, and fork COUNT stops being a difficulty dial — it cannot be, if every word is to be
  // a decision. It costs a careful reader nothing, since a fork is only paid for by someone who
  // takes it.
  const forkOf = (cell: Cell): { from: Cell; to: Cell; size: number }[] =>
    branchesOf(path, cols, rows, open).filter((b) => key(b.from) === key(cell));

  // Sealing may not strand a cell or collapse the braid, so both are checked before it happens.
  const canSeal = (from: Cell, to: Cell): boolean => {
    const without = new Set(open);
    without.delete(edgeKey(from, to));
    if (routesBetween(path[0], target, cols, rows, without).length < needRoutes) return false;
    return reachable(path[0], cols, rows, without).size === cols * rows;
  };

  path.forEach((cell, i) => {
    const last = i === path.length - 1;
    // The last word has nothing to choose: the gate is the only way on.
    const keep = last ? 0 : 1;
    for (const extra of shuffle(forkOf(cell), rng).slice(keep)) {
      if (canSeal(extra.from, extra.to)) open.delete(edgeKey(extra.from, extra.to));
    }
    if (!last && forkOf(cell).length === 0) {
      const nb = shuffle(neighboursOf(cell, cols, rows), rng)
        .find((n) => n[0] >= cell[0] && !onPath.has(key(n)));
      if (nb) open.add(edgeKey(cell, nb));
    }
  });

  // Checked, not assumed: passes 3 and 4 both add and remove corridors, so the braid count has to
  // be re-counted after them rather than trusted from pass 2.
  const finalRoutes = routesBetween(path[0], target, cols, rows, open).length;
  const branches = branchesOf(path, cols, rows, open);
  const stubs = branches.filter((b) => b.size < LABYRINTH.minDeadEndLen);
  const orphans = cols * rows - reachable(path[0], cols, rows, open).size;
  // Coverage, not a count: how many WORDS offer a choice. `forks` is a floor now — the board must
  // put a decision in front of the child at least that many times — and it is checked against the
  // path rather than against the braid, since a branch hanging off a cell nobody stands on is not
  // a fork the player will ever meet.
  const decisions = new Set(branches.map((b) => key(b.from))).size;
  const ok = finalRoutes >= needRoutes
    && stubs.length === 0
    && orphans === 0
    && decisions >= Math.min(forksFor(round), path.length - 1);
  return ok ? { open, routes: finalRoutes } : null;
}

/**
 * Lay out one board's shape for `round`, given the sentence that must run along its path.
 *
 * `cols` comes out as `min(ladderCols, words.length)` and that is deliberate (see `config.ts`): the
 * path has to reach the right-hand edge, so the maze can never be wider than the sentence is long.
 * Without it ja/ko/th — whose sentences top out at four path cells — would have no dealable board
 * from round 2 on.
 */
export function buildMaze(
  round: number,
  words: string[],
  postAfter: number,
  rng: Rng = Math.random,
): Omit<LabyrinthBoard, 'grid'> | null {
  const [ladderCols, rows] = gridFor(round);
  // The grid is narrower than the path is long, and that subtraction IS the difficulty ladder (see
  // LABYRINTH.bends). The path spends `cols - 1` steps crossing, so `path.length - cols` steps are
  // vertical: ask for more bends, take away more columns. The first build subtracted exactly one,
  // which pinned every board at a single vertical step — "mostly straight lines with one or two
  // turns", which is what play-testing reported.
  //
  // But subtracting without limit makes it WORSE, and that is the trap. Turns come from
  // alternating, so a board can only turn about `2 x min(verticals, cols)` times: strip the columns
  // past halfway and the surplus vertical steps have nowhere to go but on top of each other, one
  // long corridor with a kink at each end. Measured, round 5 fell from four turns to two that way.
  // So `ceil(len / 2)` is a floor, which is exactly the point where verticals and columns balance
  // and the turn count peaks.
  //
  // Floored again at 2 columns and capped by the ladder's own width, so a short sentence still
  // reaches the right-hand edge. ja/ko/th live near that floor: their sentences top out at four
  // tiles, so they take the bends they can afford rather than the rung's full ask.
  const len = words.length;
  const balanced = Math.max(2, Math.ceil(len / 2));
  const cols = Math.min(ladderCols, Math.max(balanced, len - bendsFor(round)));
  if (len > cols * rows) return null;

  for (let attempt = 0; attempt < LABYRINTH.dealAttempts; attempt++) {
    const path = carvePath(cols, rows, len, rng);
    if (!path) continue;
    const carved = carveMaze(round, path, cols, rows, rng);
    if (!carved) continue;
    const { open } = carved;
    return {
      round,
      cols,
      rows,
      path,
      open,
      entryRow: path[0][1],
      gateRow: path[path.length - 1][1],
      sentence: words.slice(),
      postAfter,
      timeMs: timeForRound(round),
    };
  }
  return null;
}

export { pathCellsFor, timeForRound };
